import { trpc } from "@/lib/trpc";
import {
  CheckCircle2,
  ExternalLink,
  Loader2,
  MailCheck,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";

export default function GoogleWorkspacePanel() {
  const status = trpc.googleWorkspace.status.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const deliveryTest = trpc.googleWorkspace.sendTest.useMutation({
    onSuccess: ({ messageId }) => {
      chiNotify.success("Google Workspace delivery verified", {
        description: `Gmail accepted the test message (${messageId.slice(0, 12)}…).`,
      });
      void status.refetch();
    },
    onError: error =>
      chiNotify.error("Delivery test failed", { description: error.message }),
  });

  const connection = status.data;
  const ready = Boolean(connection?.connected);
  const testDeliveryEnabled = connection?.testDeliveryEnabled !== false;
  const checkingConnection = status.isLoading || status.isFetching;

  return (
    <div style={{ padding: "clamp(1.25rem, 3vw, 2.5rem)", overflowY: "auto" }}>
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <div style={{ marginBottom: "1.75rem" }}>
          <p style={{ margin: "0 0 0.45rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Secure delivery
          </p>
          <h1 style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 4vw, 3rem)", letterSpacing: "0.02em" }}>
            Google Workspace
          </h1>
          <p style={{ maxWidth: 680, margin: "0.65rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.7 }}>
            Gmail sends directly through Google’s API using an owner-authorized OAuth grant. The refresh token is encrypted server-side and never reaches the browser.
          </p>
        </div>

        {status.error ? (
          <section role="alert" style={{ padding: "1.5rem", borderRadius: "1.25rem", background: "linear-gradient(145deg,rgba(239,68,68,.08),rgba(13,18,32,.98))", border: "1px solid rgba(239,68,68,.25)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "0.9rem" }}>
              <TriangleAlert size={22} color="#FCA5A5" style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <h2 style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1rem" }}>Secure owner session required</h2>
                <p style={{ margin: "0.45rem 0 1rem", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>{status.error.message}</p>
                <button type="button" onClick={() => window.location.assign("/login")} style={{ minHeight: 42, padding: "0.65rem 1rem", border: "1px solid rgba(233,30,140,.48)", borderRadius: "0.65rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: "pointer" }}>Sign in as project owner</button>
              </div>
            </div>
          </section>
        ) : (

        <section style={{ background: "linear-gradient(145deg,rgba(233,30,140,.09),rgba(13,18,32,.96) 38%,rgba(0,212,255,.06))", border: "1px solid rgba(255,255,255,.1)", borderRadius: "1.25rem", overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,.28)" }}>
          <div style={{ padding: "1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,.08)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.9rem" }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, display: "grid", placeItems: "center", background: ready ? "rgba(34,197,94,.12)" : "rgba(245,166,35,.12)", border: `1px solid ${ready ? "rgba(34,197,94,.35)" : "rgba(245,166,35,.35)"}`, color: ready ? "#4ADE80" : "#F5A623" }}>
                {ready ? <ShieldCheck size={23} /> : <TriangleAlert size={22} />}
              </div>
              <div>
                <p style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1rem", fontWeight: 750 }}>
                  {status.isLoading ? "Checking connection…" : ready ? "Gmail connected" : "Gmail connection unavailable"}
                </p>
                <p style={{ margin: "0.25rem 0 0", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
                  {connection?.senderEmail ?? "No sender mailbox is connected yet."}
                </p>
              </div>
            </div>

            <div style={{ padding: "0.65rem 0.85rem", border: "1px solid rgba(0,212,255,.28)", borderRadius: "0.7rem", color: "#68E6FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 700 }}>
              Direct Gmail API
            </div>
          </div>

          <div style={{ padding: "1.5rem", display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))" }}>
            <StatusCard
              label="Delivery path"
              value={connection?.deliveryMode === "direct_oauth" ? "Direct OAuth" : connection?.deliveryMode === "connector" ? "Replit connector" : "Unavailable"}
              good={connection?.deliveryMode === "direct_oauth"}
            />
            <StatusCard
              label="Authorized mailbox"
              value={connection?.connected ? "Connected" : "Not connected"}
              good={Boolean(connection?.connected)}
            />
            <StatusCard
              label="Last verified"
              value={connection?.lastVerifiedAt ? new Date(connection.lastVerifiedAt).toLocaleString() : "Not yet verified"}
              good={Boolean(connection?.lastVerifiedAt)}
            />
          </div>

          {connection?.lastError && (
            <div role="alert" style={{ margin: "0 1.5rem 1.25rem", padding: "0.9rem 1rem", borderRadius: "0.75rem", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", color: "#FCA5A5", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.55 }}>
              {connection.lastError}
            </div>
          )}

          <div style={{ padding: "1.25rem 1.5rem", borderTop: "1px solid rgba(255,255,255,.08)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)" }}>
              <MailCheck size={16} color="#00D4FF" />
               {!ready
                  ? "Authorize the configured Workspace sender directly with Google."
                 : testDeliveryEnabled
                 ? "Send a controlled message to the signed-in owner account."
                 : "Delivery tests are disabled in production."}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap" }}>
              {!status.isLoading && !ready && (
                <>
                  <div style={{ flexBasis: "100%", maxWidth: 620, padding: "0.85rem 1rem", borderRadius: "0.75rem", background: "rgba(255,255,255,.035)", border: "1px solid rgba(255,255,255,.08)", color: "#CBD5E1", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>
                    Authorize the configured sender with Google. The application requests only the Gmail send and mailbox-identity scopes and stores the refresh token encrypted.
                  </div>
                  <a
                    href="/auth/google-workspace/start"
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", minHeight: 44, padding: "0.68rem 1rem", borderRadius: "0.65rem", border: "1px solid rgba(233,30,140,.48)", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "#FFFFFF", textDecoration: "none", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750 }}
                  >
                    <ExternalLink size={14} />
                    Authorize Gmail
                  </a>
                  <button
                    type="button"
                    onClick={() => void status.refetch()}
                    disabled={checkingConnection}
                    style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", minHeight: 44, padding: "0.68rem 1rem", borderRadius: "0.65rem", border: "1px solid rgba(0,212,255,.35)", background: "rgba(0,212,255,.09)", color: "#68E6FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: checkingConnection ? "not-allowed" : "pointer", opacity: checkingConnection ? 0.5 : 1 }}
                  >
                    {checkingConnection ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    Check again
                  </button>
                </>
              )}
              {ready && (
                <button
                  type="button"
                  onClick={() => deliveryTest.mutate()}
                  disabled={!testDeliveryEnabled || deliveryTest.isPending}
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", minHeight: 44, padding: "0.68rem 1rem", borderRadius: "0.65rem", border: "1px solid rgba(0,212,255,.35)", background: "rgba(0,212,255,.09)", color: "#68E6FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 750, cursor: !testDeliveryEnabled || deliveryTest.isPending ? "not-allowed" : "pointer", opacity: !testDeliveryEnabled || deliveryTest.isPending ? 0.5 : 1 }}
                >
                  {deliveryTest.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Verify delivery
                </button>
              )}
            </div>
          </div>
        </section>
        )}
      </div>
    </div>
  );
}

function StatusCard({ label, value, good }: { label: string; value: string; good: boolean }) {
  return (
    <div style={{ padding: "1rem", borderRadius: "0.85rem", background: "rgba(255,255,255,.035)", border: "1px solid rgba(255,255,255,.075)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.45rem", color: good ? "#4ADE80" : "#F5A623" }}>
        <CheckCircle2 size={14} />
        <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "var(--chi-body-compact)", fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase" }}>{label}</span>
      </div>
      <p style={{ margin: 0, color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.45 }}>{value}</p>
    </div>
  );
}
